import { create } from "zustand";
import type { Session, AttachedDocument, ProviderPreference } from "../lib/session";
import { writeAndCommit, deleteFile, commit as gitCommit } from "../lib/git-store";

// Channel templates — saved "channel shapes" you can spin new
// channels from. Captures the configuration knobs (mode, whiteboard
// seed, attached docs, provider prefs, tool/MP toggles) but NOT the
// chat history, identity, or per-channel state (sync, encrypted
// keys, pinning, token totals, undo history). A template is a
// reusable starting point, not a snapshot.
//
// Storage: per-browser localStorage. Doesn't sync — same boat as the
// library; templates are personal navigation state, not channel
// content.

const STORAGE_KEY = "playground.channel_templates";
const STORAGE_DEFAULT_KEY = "playground.default_template_id";

export interface ChannelTemplate {
  id: string;
  name: string;
  created_at: string;
  // Snapshot of the channel-shape knobs at save time:
  mode?: "chat" | "brainstorm";
  document?: string;
  attached_documents?: AttachedDocument[];
  brainstorm_mp_enabled?: boolean;
  provider_preference?: ProviderPreference;
  tools_enabled?: boolean;
  mosh_pit_enabled?: boolean;
}

type TemplateState = {
  templates: ChannelTemplate[];
  /** Id of the template applied when "+" creates a new channel.
   *  null = no default; new channels start blank. */
  defaultId: string | null;
  saveTemplate: (name: string, source: Session) => string;
  removeTemplate: (id: string) => void;
  setDefault: (id: string | null) => void;
};

function load(): ChannelTemplate[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChannelTemplate[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[templates] load failed:", e);
    return [];
  }
}

function persist(list: ChannelTemplate[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn("[templates] persist failed:", e);
  }
}

function loadDefault(): string | null {
  if (typeof localStorage === "undefined") return null;
  try { return localStorage.getItem(STORAGE_DEFAULT_KEY); } catch { return null; }
}

function persistDefault(id: string | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (id === null) localStorage.removeItem(STORAGE_DEFAULT_KEY);
    else localStorage.setItem(STORAGE_DEFAULT_KEY, id);
  } catch (e) {
    console.warn("[templates] persistDefault failed:", e);
  }
}

// Git-canonical write-through. Templates land at
// data/templates/<id>.json. The default-id pointer rides on
// data/prefs/default-template.json so a restore picks up which
// template is the default along with the templates themselves.
function shortId(id: string): string {
  return id.slice(0, 8);
}

function gitWriteTemplate(t: ChannelTemplate, message: string): void {
  const path = `data/templates/${t.id}.json`;
  const body = JSON.stringify(t, null, 2);
  void writeAndCommit(path, body, message).catch((e) => {
    console.warn("[git-store] template write failed:", t.id, e);
  });
}

function gitDeleteTemplate(id: string, name: string): void {
  const path = `data/templates/${id}.json`;
  void (async () => {
    try {
      const removed = await deleteFile(path);
      if (removed) await gitCommit(`delete template ${shortId(id)} (${name})`);
    } catch (e) {
      console.warn("[git-store] template delete failed:", id, e);
    }
  })();
}

function gitWriteDefault(id: string | null): void {
  const path = `data/prefs/default-template.json`;
  const body = JSON.stringify({ defaultId: id }, null, 2);
  void writeAndCommit(path, body, `update prefs (default-template → ${id ? shortId(id) : "none"})`)
    .catch((e) => {
      console.warn("[git-store] default-template write failed:", e);
    });
}

export const useTemplateStore = create<TemplateState>((set, get) => ({
  templates: load(),
  defaultId: loadDefault(),

  saveTemplate: (name, source) => {
    const t: ChannelTemplate = {
      id: crypto.randomUUID(),
      name: name.trim() || "untitled template",
      created_at: new Date().toISOString(),
      mode: source.mode,
      document: source.document,
      attached_documents: source.attached_documents,
      brainstorm_mp_enabled: source.brainstorm_mp_enabled,
      provider_preference: source.provider_preference,
      tools_enabled: source.tools_enabled,
      mosh_pit_enabled: source.mosh_pit_enabled,
    };
    const next = [t, ...get().templates];
    persist(next);
    set({ templates: next });
    gitWriteTemplate(t, `create template ${shortId(t.id)} (${t.name})`);
    return t.id;
  },

  removeTemplate: (id) => {
    const prev = get().templates.find((t) => t.id === id);
    const next = get().templates.filter((t) => t.id !== id);
    persist(next);
    // If the deleted template was the default, clear that too —
    // otherwise we'd point at a ghost.
    const clearDefault = get().defaultId === id;
    if (clearDefault) persistDefault(null);
    set({ templates: next, defaultId: clearDefault ? null : get().defaultId });
    if (prev) gitDeleteTemplate(id, prev.name);
    if (clearDefault) gitWriteDefault(null);
  },

  setDefault: (id) => {
    // Toggle off if same id passed twice — clicking ★ on the
    // already-default template removes the default.
    const next = get().defaultId === id ? null : id;
    persistDefault(next);
    set({ defaultId: next });
    gitWriteDefault(next);
  },
}));
